import * as React from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';

import { withModal } from 'components/modal/ModalHOC';

import GalleryModal from 'components/gallery-modal/gallery-modal/GalleryModal';
import ConsentPlaceholder from 'components/consent-placeholder/ConsentPlaceholder';

import ContentListResultsContext from 'modules/lists-content/context/ContentListResultsContext';
import TileStandardCarouselItemPlaceholder from './placeholder/TileStandardCarouselItemPlaceholder';
import TileStandardCarouselItemOverlay from './overlay/TileStandardCarouselItemOverlay';

import { WITH_GALLERY_MODAL_CONFIG } from 'utils/gallery';

import type { ImageCandidate, ImageStandard, State, TileStandardCarouselType } from 'types';
import type { ConsentReducerSupportedChannels } from 'redux-store/types/ConsentReducer.type';

import './tile-standard-carousel-item.scss';


type Props = {
  type: TileStandardCarouselType,
  activeTab: string,
  items: Array<ImageCandidate>,
  item: ImageStandard,
  index: number,
  galleryModalIndexStart: number,
  isLoading: boolean,
  classNameItem?: string,
  onLastPage: () => void
};

const ImageWithModal = withModal(
  <div />,
  {
    ...WITH_GALLERY_MODAL_CONFIG,
    modalContent: (props) => (
      <ContentListResultsContext.Consumer>
        {(results) => <GalleryModal {...props} items={results} />}
      </ContentListResultsContext.Consumer>
    )
  }
);

const TileStandardCarouselItem = (
  {
    type,
    activeTab,
    items,
    item,
    index,
    isLoading,
    galleryModalIndexStart,
    classNameItem,
    onLastPage
  }: Props
): JSX.Element => {
  const userConsents = useSelector((state: State) => state.consentReducer.consents);

  const containerClasses = classnames('tile-standard-carousel-item', classNameItem, {
    'tile-standard-carousel-item--is-loading': isLoading && userConsents[item.type],
    [`tile-standard-carousel-item--type-${type}`]: Boolean(type)
  });

  if (item.type === 'placeholder') {
    return (
      <div className={`${containerClasses} tile-standard-carousel-item__placeholder`}>
        <div className="tile-standard-carousel-item__content">
          <TileStandardCarouselItemPlaceholder
            type={type}
          />
        </div>
      </div>
    );
  }

  if (!userConsents[activeTab]) {
    return (
      <div className={containerClasses}>
        <div className="tile-standard-carousel-item__consent-placeholder">
          <ConsentPlaceholder
            withLearnMore
            link={item.url}
            type={activeTab as ConsentReducerSupportedChannels}
          />
        </div>
      </div>
    );
  }

  if (item.type === 'loading') {
    return (
      <div className={containerClasses}>
        <div className="tile-standard-carousel-item__content">
          <div className="tile-standard-carousel-item__image" />
        </div>
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      <div className="tile-standard-carousel-item__content">
        <ImageWithModal
          className="tile-standard-carousel-item__image lazyload"
          data-src={item.assetUrl}
          modalContentProps={{
            theme: 'white',
            items: items ? items.filter(i => i.type !== 'placeholder') : [],
            currentPosition: index + galleryModalIndexStart,
            onLastPage
          }}
          style={{
            ...(!isLoading ? { backgroundImage: `url(${item.assetUrl})` } : null)
          }}
        >
          <TileStandardCarouselItemOverlay
            type={item.type}
          />
        </ImageWithModal>
      </div>
    </div>
  );
};

export default TileStandardCarouselItem;
